import { List, Record, Set } from "immutable";
import { concat, has, isArray, isNil, isObject, isString, map as loMap, maxBy, range, uniq, without } from "lodash";

import { OUTLINE_MIN_SCROLLABLE_COLUMNS_WIDTH } from "../../constants";
import { colorMap, colorMapHexLookup } from "../../constants/colorOptions";
import interfaceMessages from "../../constants/interfaceMessages";
import outlineColumnIds, { stickyColumnIds } from "../../constants/outlineColumnIds";
import pasteDataTypes from "../../constants/pasteDataTypes";
import Dimensions, { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import TableCellTypes from "../../constants/tableCellTypes";
import tableRowTypes from "../../constants/tableRowTypes";
import dateByAddingSpan from "../../modules/definition/calendar/dateFunctions/dateByAddingSpan";
import { dateWithString, endDateWithString } from "../../modules/definition/calendar/dateFunctions/dateWithString";
import {
  getReverseTimespan,
  timespanForString
} from "../../modules/definition/calendar/dateFunctions/timespanFunctions";
import { immutableRecords } from "../../types";
import preprocessorQuery from "../../utils/preprocessorQuery";
import uniqueId from "../../utils/uniqueId";

/**
 *
 * @param {TableCellGroupRecord} group
 * @param {TableCellPosition} cell
 */
const isCellInGroup = (group, cell) =>
  group &&
  cell.row >= group.topLeft.row &&
  cell.row <= group.bottomRight.row &&
  cell.column >= group.topLeft.column &&
  cell.column <= group.bottomRight.column;

/**
 *
 * @param {TableCellGroupRecord} group
 * @param {number} rowIndex
 */
const isRowInGroup = (group, rowIndex) => group && rowIndex >= group.topLeft.row && rowIndex <= group.bottomRight.row;

/**
 *
 * @param {TableCellSelectionRecord} selection
 * @param {TableCellPosition} cell
 */
const isCellSelected = (selection, cell) =>
  Boolean(selection && selection.groups.find(group => isCellInGroup(group, cell)));

/**
 *
 * @param {TableCellSelectionRecord} selection
 * @param {number} rowIndex
 */
const isRowPartOfSelection = (selection, rowIndex) =>
  Boolean(selection && selection.groups.find(group => isRowInGroup(group, rowIndex)));

/**
 *
 * @param {TableCellSelectionRecord} selection
 */
const rowsInSelection = selection => {
  let rows = [];
  if (selection) {
    selection.groups.forEach(group => {
      for (let r = group.topLeft.row; r <= group.bottomRight.row; r++) {
        rows.push(r);
      }
    });
  }
  return List(uniq(rows));
};

/**
 *
 * @param {TableCellSelectionRecord} selection
 * @param {TableCellPosition} cell
 */
const isCellSingleSelection = (selection, cell) =>
  Boolean(
    selection &&
      selection.groups.size === 1 &&
      selection.groups.find(
        group =>
          cell.row === group.topLeft.row &&
          cell.row === group.bottomRight.row &&
          cell.column === group.topLeft.column &&
          cell.column === group.bottomRight.column
      )
  );

/**
 *
 * @param {TableCellSelectionRecord} selection
 */
const mergeSelectionIfRect = selection => {
  if (!selection.groups || !selection.groups.size) {
    return selection;
  }
  // First, calculate the boundaries of our selection
  const left = selection.groups.reduce(
    (val, group) => (val === null ? group.topLeft.column : Math.min(val, group.topLeft.column)),
    null
  );
  const top = selection.groups.reduce(
    (val, group) => (val === null ? group.topLeft.row : Math.min(val, group.topLeft.row)),
    null
  );
  const right = selection.groups.reduce(
    (val, group) => (val === null ? group.bottomRight.column : Math.max(val, group.bottomRight.column)),
    null
  );
  const bottom = selection.groups.reduce(
    (val, group) => (val === null ? group.bottomRight.row : Math.max(val, group.bottomRight.row)),
    null
  );

  // Now iterate through grid and check that all are selected...
  for (let row = top; row <= bottom; row++) {
    for (let column = left; column <= right; column++) {
      // if anything inside the grid is not selected, return the original selection
      if (!isCellSelected(selection, immutableRecords.TableCellPositionRecord({ row, column }))) {
        return selection;
      }
    }
  }

  // If we reach here, we had every single cell, so return a new selection instead
  return immutableRecords.TableCellSelectionRecord({
    groups: List([
      immutableRecords.TableCellGroupRecord({
        topLeft: immutableRecords.TableCellPositionRecord({ row: top, column: left }),
        bottomRight: immutableRecords.TableCellPositionRecord({ row: bottom, column: right })
      })
    ])
  });
};

const allCellsInGroupExceptCell = (group, exceptCell) => {
  let cells = [];
  for (let row = group.topLeft.row; row <= group.bottomRight.row; row++) {
    for (let column = group.topLeft.column; column <= group.bottomRight.column; column++) {
      if (row !== exceptCell.row || column !== exceptCell.column) {
        cells.push(immutableRecords.TableCellPositionRecord({ row, column }));
      }
    }
  }
  return Set(cells);
};

/**
 *
 * @param {TableCellSelectionRecord} selection
 */
const selectionWithoutCell = (selection, removedCell) => {
  if (!selection.groups || !selection.groups.size) {
    return selection;
  }

  // Create a list of every selected cell
  const allCells = selection.groups
    .map(g => allCellsInGroupExceptCell(g, removedCell))
    .toSet()
    .flatten()
    .toList();

  const newSelection = immutableRecords.TableCellSelectionRecord({
    groups: allCells.map(c =>
      immutableRecords.TableCellGroupRecord({
        topLeft: c,
        bottomRight: c
      })
    )
  });

  return mergeSelectionIfRect(newSelection);
};

// ---------------------------------------------------------------------------------------------
// Copy/Paste Utility
// ---------------------------------------------------------------------------------------------

const matchingOptionForText = (options, text) => {
  if (options && options.size) {
    // First try to match by value
    let match = options.find(o => o.value === text);
    if (!match) {
      // Then try to match by label (case insensitive)
      const lowerText = text.toLowerCase();
      match = options.find(o => o.label.toLowerCase() === lowerText);
    }
    if (match) {
      return match;
    }
    return null;
  }
  return null;
};

const matchingMultipleOptionsForText = (options, fullText) => {
  if (options && options.size) {
    return List(fullText.split(",")).map(text => {
      // First try to match by value
      let match = options.find(o => o.value === text);
      if (!match) {
        // Then try to match by label (case insensitive)
        const lowerText = text.toLowerCase();
        match = options.find(o => o.label.toLowerCase() === lowerText);
      }
      if (match) {
        return match.value;
      }
      return null;
    });
  }
  return null;
};

const matchInColorMap = value => {
  if (value in colorMap) {
    return value;
  }
  if (value in colorMapHexLookup) {
    return colorMapHexLookup[value];
  }
  return null;
};

const matchingOptionForColor = (options, value, matchNamedColorByValue = false) => {
  const lowerValue = value.toLowerCase();
  if (options && options.size) {
    // First try to match a named color by ID (value)
    let match = options.find(o => o.value === value);
    // Then try to match by label
    if (!match) {
      match = options.find(o => o.label.toLowerCase() === lowerValue);
    }
    // Then try to match by color
    if (!match && matchNamedColorByValue) {
      match = options.find(o => o.color === value || colorMap[o.color] === value);
    }

    if (match) {
      return match.value;
    }
  }
  // It doesn't match any of our named colours, so let's try to match
  // one of the custom colors instead
  return matchInColorMap(value);
};

const matchingOptionForType = (options, value) => {
  const lowerValue = value.toLowerCase();
  if (options && options.size) {
    // First try to match a named color by ID (value)
    let match = options.find(o => o.value === value);
    // Then try to match by label
    if (!match) {
      match = options.find(o => o.label.toLowerCase() === lowerValue);
    }

    if (match) {
      return match.value;
    }
  }
  // It doesn't match any of our named colours, so let's try to match
  // one of the custom colors instead
  return matchInColorMap(value);
};

const matchingOptionForObject = (options, obj) => {
  if (options && options.size) {
    // First try to match by value
    let match = options.find(o => o.value === obj.id);
    if (!match) {
      // Then try to match by label (case sensitive)
      if (has(obj, "label")) {
        const text = obj.label;
        match = options.find(o => o.label === text);
      }
    }
    if (match) {
      return match.value;
    }
    return null;
  }
  return null;
};

const matchingMultipleOptionsForObjectArray = (options, arr) => {
  if (options && options.size) {
    return List(
      loMap(arr, obj => {
        return matchingOptionForObject(options, obj);
      })
    );
  }
  return null;
};

// ---------------------------------------------------------------------------------------------
// Copying Data
// ---------------------------------------------------------------------------------------------

const textValueGivenOptions = (options, value) => {
  if (Record.isRecord(value)) {
    if (value.has("tooltip")) {
      // For now, give tooltip precedence, as it is the full name of entities shown as avatars
      return value.tooltip;
    } else if (value.has("label")) {
      // Otherwise, if the record has a label, use that
      return value.label;
    } else {
      // eslint-disable-next-line no-console
      console.log(`--- COPY ERROR: No string conversion implemented for record:`);
      // eslint-disable-next-line no-console
      console.dir(value.toJS());
      return "";
    }
  } else {
    // For string values, convert them to their label instead of id if they are from a list
    // of options
    const match = matchingOptionForText(options, value);
    // Use match if we can, otherwise lookup custom color name.
    return match ? match.label : String(value);
  }
};

const copyValueForTextCell = (cell, options) => {
  // Treat color differently
  switch (cell.columnId) {
    case outlineColumnIds.color: {
      const stringValue = textValueGivenOptions(options, cell.value);
      if (stringValue in colorMap) {
        return colorMap[stringValue];
      } else if (cell.value in colorMap) {
        return colorMap[cell.value];
      }
      return stringValue;
    }
    case outlineColumnIds.tag: {
      const stringValue = cell.value ? cell.value.map(t => t.name).join(",") : "";
      return stringValue;
    }
  }

  // If it is a list of values, merge all the values
  if (List.isList(cell.value)) {
    // Convert each item in the list to a value
    return cell.value.map(val => textValueGivenOptions(options, val)).join(",");
  }

  // Otherwise, just convert it to text
  return textValueGivenOptions(options, cell.value);
};

const copyValueForJsonCell = (cell, column, options) => {
  return {
    value: cell.value && cell.value.toJS ? cell.value.toJS() : cell.value,
    column: column.toJS(),
    text: copyValueForTextCell(cell, options)
  };
};

/**
 *
 * @param {ClipboardEvent} e
 * @param {List} columns
 * @param {TableCellSelectionRecord} group
 * @param {List} rows
 */
const copyTableDataToClipboard = (e, columns, group, rows, getCellOptions) => {
  const isMultipleCells =
    group.topLeft.row !== group.bottomRight.row || group.topLeft.column !== group.bottomRight.column;

  const copyRows = rows.slice(group.topLeft.row, group.bottomRight.row + 1);

  let plainData = null;
  let htmlData = null;

  if (isMultipleCells) {
    // If we are copying multiple cells, we want to extract a 2x2 grid of all the cells
    // for text and HTML
    const textGrid = copyRows.map(row =>
      row.cells.slice(group.topLeft.column, group.bottomRight.column + 1).map(cell => {
        return copyValueForTextCell(cell, getCellOptions(cell.rowId, cell.columnId));
      })
    );
    plainData = textGrid.map(row => row.join("\t")).join("\n");
    htmlData = `<table>${textGrid
      .map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join("")}</tr>`)
      .join("\n")}</table>`;
  } else {
    // For a single cell, just copy the plain text value so that users can paste it
    // without unnecessary HTML formatting
    const cell = copyRows.first().cells.get(group.topLeft.column);
    plainData = copyValueForTextCell(cell, getCellOptions(cell.rowId, cell.columnId));
    htmlData = plainData;
  }

  // Our internal data version always sits in an array of table cells, even if it is a
  // single cell only.
  const jsonGrid = copyRows.map(row =>
    row.cells.slice(group.topLeft.column, group.bottomRight.column + 1).map(cell => {
      const column = columns.get(cell.column);
      return copyValueForJsonCell(cell, column, getCellOptions(cell.rowId, cell.columnId));
    })
  );

  const aeonTableData = JSON.stringify(jsonGrid.toJS());

  const clipboardData = e.clipboardData || window.clipboardData;
  clipboardData.setData(pasteDataTypes.HTML, htmlData);
  clipboardData.setData(pasteDataTypes.PLAINTEXT, plainData);
  clipboardData.setData(pasteDataTypes.AEON_TABLE, aeonTableData);

  // This will not be needed until ClipboardAPI multi-format is better supported by
  // browsers

  // // This is a special function we add to our custom object when we need to handle
  // // things via the Clipboard API rather than catching the ClipboardEvent
  // if (clipboardData.writeToClipboard) {
  //   clipboardData.writeToClipboard();
  // }
};

// ---------------------------------------------------------------------------------------------
// Pasting Data
// ---------------------------------------------------------------------------------------------

const gridFromHtml = html => {
  return Array.from(new DOMParser().parseFromString(html, "text/html").querySelectorAll("tr"), row =>
    [...row.cells].map(cell => cell.textContent.replace(/^[\s\n]+|[\s\n]+$/g, ""))
  );
};

const gridFromPlainText = text => loMap(text.split(/\r?\n/), line => line.split("\t"));

/**
 *
 * @param {ClipboardEvent} e
 */
const gridFromClipboardEvent = e => {
  const clipboardData = e.clipboardData || window.clipboardData;
  const availableTypes = clipboardData.types;
  if (availableTypes.indexOf(pasteDataTypes.AEON_TABLE) >= 0) {
    const pasteJsonData = clipboardData.getData(pasteDataTypes.AEON_TABLE);
    return {
      pasteDataType: pasteDataTypes.AEON_TABLE,
      gridData: JSON.parse(pasteJsonData)
    };
  }
  if (availableTypes.indexOf(pasteDataTypes.HTML) >= 0) {
    const pastedData = clipboardData.getData(pasteDataTypes.HTML);
    const dataGrid = gridFromHtml(pastedData);
    if (dataGrid.length >= 1 && dataGrid[0].length >= 1) {
      return {
        pasteDataType: pasteDataTypes.HTML,
        gridData: dataGrid
      };
    }
  }
  if (availableTypes.indexOf(pasteDataTypes.PLAINTEXT) >= 0) {
    const pastedData = clipboardData.getData(pasteDataTypes.PLAINTEXT);
    return {
      pasteDataType: pasteDataTypes.PLAINTEXT,
      gridData: gridFromPlainText(pastedData)
    };
  }
  return null;
};

const pastedTextToBool = pastedText => {
  if (isNil(pastedText)) {
    return false;
  }
  const pastedLowerCase = pastedText.toLowerCase();
  const pastedNumber = Number(pastedText);
  return (
    pastedLowerCase === "true" ||
    pastedLowerCase === "yes" ||
    pastedLowerCase === "on" ||
    (!isNil(pastedNumber) && !isNaN(pastedNumber) && pastedNumber !== 0)
  );
};

const matchingForTag = value => {
  let result = null;
  if (value) {
    if (isArray(value)) {
      result = value.map(v => v.name);
    } else if (isString(value)) {
      result = value.split(",").map(v => v.trim());
    }
  }
  return result;
};

const isNilOrEmpty = val => val.length === 0 || isNil(val) || (has(val, "text") && isNilOrEmpty(val.text));

const pasteValueForJsonCell = (cell, column, calendar, pastedData, getCellOptions) => {
  const options = getCellOptions(cell.rowId, cell.columnId);

  // Dates and colors need special handling
  switch (cell.columnId) {
    case outlineColumnIds.start:
    case outlineColumnIds.latestStart: {
      return dateWithString(calendar, pastedData.text);
    }
    case outlineColumnIds.duration: {
      return timespanForString(pastedData.text, calendar);
    }
    case outlineColumnIds.earliestEnd: {
      return endDateWithString(calendar, pastedData.text, cell.startDate, cell.durationToEarliestEnd);
    }
    case outlineColumnIds.end: {
      return endDateWithString(calendar, pastedData.text, cell.startDate, cell.duration);
    }
    case outlineColumnIds.color: {
      return matchingOptionForColor(options, pastedData.value, false);
    }
    case outlineColumnIds.type: {
      return matchingOptionForType(options, pastedData.value);
    }
    case outlineColumnIds.tag: {
      return matchingForTag(pastedData.value);
    }
  }

  // Default: either match an option, or return the pasted text plain
  if (options) {
    if (column.cellType === TableCellTypes.multipleReference) {
      const matches = matchingMultipleOptionsForObjectArray(options, pastedData.value);
      return matches;
    } else if (isObject(pastedData.value)) {
      const match = matchingOptionForObject(options, pastedData.value);
      return match ? match : null;
    } else {
      const match = matchingOptionForText(options, pastedData.value);
      return match ? match.value : null;
    }
  }

  // Numbers and bools need format changes
  switch (column.cellType) {
    case TableCellTypes.number:
      return Number(pastedData.text);
    case TableCellTypes.checkbox:
      return pastedTextToBool(pastedData.text);
  }

  // Otherwise, just return the text value
  return pastedData.text;
};

const pasteValueForTextCell = (cell, column, calendar, pastedText, getCellOptions) => {
  const options = getCellOptions(cell.rowId, cell.columnId);

  // Dates and colors need special handling
  switch (cell.columnId) {
    case outlineColumnIds.start:
    case outlineColumnIds.latestStart: {
      return dateWithString(calendar, pastedText);
    }
    case outlineColumnIds.duration: {
      return timespanForString(pastedText, calendar);
    }
    case outlineColumnIds.earliestEnd: {
      return endDateWithString(calendar, pastedText, cell.startDate, cell.durationToEarliestEnd);
    }
    case outlineColumnIds.end: {
      return endDateWithString(calendar, pastedText, cell.startDate, cell.duration);
    }
    case outlineColumnIds.color: {
      return matchingOptionForColor(options, pastedText, true);
    }
    case outlineColumnIds.type: {
      return matchingOptionForType(options, pastedText);
    }
    case outlineColumnIds.tag: {
      return matchingForTag(pastedText);
    }
  }

  // Numbers and bools need format changes
  switch (column.cellType) {
    case TableCellTypes.number:
      return Number(pastedText);
    case TableCellTypes.checkbox:
      return pastedTextToBool(pastedText);
  }

  // Default: either match an option, or return the pasted text plain
  if (options) {
    if (column.cellType === TableCellTypes.multipleReference) {
      const matches = matchingMultipleOptionsForText(options, pastedText);
      return matches;
    } else {
      const match = matchingOptionForText(options, pastedText);
      return match ? match.value : null;
    }
  }

  return pastedText;
};

/**
 *
 * @param {TableCellRecord} cell
 * @param {TableColumnRecord} column
 * @param {CalendarRecord} calendar
 * @param {*} pastedData
 * @param {String} pasteDataType
 * @param {function} getCellOptions
 */
const pasteValueForCell = (cell, column, calendar, pastedData, pasteDataType, getCellOptions) => {
  if (pasteDataType === pasteDataTypes.AEON_TABLE && isObject(pastedData)) {
    return pasteValueForJsonCell(cell, column, calendar, pastedData, getCellOptions);
  }
  return pasteValueForTextCell(cell, column, calendar, pastedData, getCellOptions);
};

const sortPasteColumns = (colIndexes, columns, firstColumnIndex) => {
  // All we care about is that endDate comes after startDate and duration
  // So let's just move it to the end
  const startDateIndex = columns.findIndex(c => c.id === outlineColumnIds.start) - firstColumnIndex;
  const durationIndex = columns.findIndex(c => c.id === outlineColumnIds.duration) - firstColumnIndex;
  const endDateIndex = columns.findIndex(c => c.id === outlineColumnIds.end) - firstColumnIndex;
  const ongoingIndex = columns.findIndex(c => c.id === outlineColumnIds.ongoing) - firstColumnIndex;

  let resultColIndexes = colIndexes;
  // Move ongoing to the first
  if (resultColIndexes.indexOf(ongoingIndex) > 0) {
    resultColIndexes = without(resultColIndexes, ongoingIndex);
    resultColIndexes.unshift(ongoingIndex);
  }

  // If end is in the list
  if (resultColIndexes.indexOf(endDateIndex) >= 0) {
    // Create an array without it
    const indexesWithEndRemoved = without(resultColIndexes, endDateIndex);
    // If we are pasting both start date and duration, we don't need to paste end date at all
    if (resultColIndexes.indexOf(startDateIndex) >= 0 && resultColIndexes.indexOf(durationIndex) >= 0) {
      return indexesWithEndRemoved;
    }
    // We are not pasting both start and duration, so the end date is relevant and needs to be included at the end
    return concat(indexesWithEndRemoved, endDateIndex);
  }
  return resultColIndexes;
};

/**
 *
 * @param {ClipboardEvent} e
 * @param {List} columns
 * @param {List} rows
 * @param {CalendarRecord} calendar
 * @param {TableCellSelectionRecord} cellSelection
 * @param {function} getCellOptions
 *
 */
const pasteDataFromClipboard = (e, columns, rows, calendar, cellSelection, getCellOptions) => {
  const clipData = gridFromClipboardEvent(e);

  if (!clipData) {
    return immutableRecords.TablePasteResultRecord({
      success: false,
      errorMessage: null // No error message needs to be shown here, we just don't have data we can use
    });
  }
  const { gridData, pasteDataType } = clipData;

  // How many rows have we selected or pasted?
  const numGridRows = gridData.length;
  const numGridCols = maxBy(gridData, row => row.length).length;

  if (cellSelection.groups.size > 1 && (numGridRows !== 1 || numGridCols !== 1)) {
    return immutableRecords.TablePasteResultRecord({
      success: false,
      errorMessage: interfaceMessages.TABLE_VIEW_PASTE_ERROR_MULTIPLE_RANGES
    });
  }

  // List of changes we will apply.
  let newRowIds = [];
  let bulkChanges = [];
  let pasteErrorColumn = null;
  let pasteDimensionsMismatch = false;

  // Iterate for all cell selections
  cellSelection.groups.forEach(group => {
    const firstColumnIndex = group.topLeft.column;
    const firstRowIndex = group.topLeft.row;

    const numSelectRows = group.bottomRight.row - group.topLeft.row + 1;
    const numSelectCols = group.bottomRight.column - group.topLeft.column + 1;

    // Do the dimensions match?
    const beyondRightEdge = firstColumnIndex + numGridCols > columns.size;
    const selectionTooSmall =
      (numSelectRows !== 1 || numSelectCols !== 1) && (numSelectRows < numGridRows || numSelectCols < numGridCols);
    if (beyondRightEdge || selectionTooSmall) {
      pasteDimensionsMismatch = true;
      return;
    }

    // Therefore, how many cells do we actually want to change?
    const numPasteRows = Math.max(numGridRows, numSelectRows);
    const numPasteCols = Math.max(numGridCols, numSelectCols);

    // Turn them into ranges so we can iterate using functions
    const rowIndexes = range(numPasteRows);
    // Sort the columns to ensure end comes after start and duration, if we are pasting both
    const colIndexes = sortPasteColumns(range(numPasteCols), columns, firstColumnIndex);

    // Iterate over all rows
    rowIndexes.forEach(rIndex => {
      // If we run out of copied rows, we will just repeat them
      const dataRowIndex = rIndex % numGridRows;
      const rowData = gridData[dataRowIndex];
      // Which row within the table are we pasting into
      const pasteRowIndex = firstRowIndex + rIndex;

      // We want to keep track of the result of date pastes
      // so that we can consider them all together.
      let pastedStart = null;
      let pastedDuration = null;
      let pastedEnd = null;
      let pastedOngoing = null;

      // Keep track of new rows which require new IDs to be generated.
      const isNewRow = pasteRowIndex >= rows.size || rows.get(pasteRowIndex).id === tableRowTypes.EMPTY;
      const row = isNewRow ? rows.last() : rows.get(pasteRowIndex);
      // We generate the new ID here so we can refer to it in other operations
      const rowId = isNewRow ? uniqueId() : row.id;

      if (isNewRow) {
        newRowIds.push(rowId);
      }

      // Iterate over all columns
      colIndexes.forEach(cIndex => {
        // If we run out of copied columns, we will just repeat them
        const dataColIndex = cIndex % numGridCols;
        const rawValue = rowData[dataColIndex];
        // Which collumn within the table are we pasting into?
        const pasteColIndex = firstColumnIndex + cIndex;

        // Access the data associated with the cell so we can get the right type.
        const column = columns.get(pasteColIndex);
        let cell = row.cells.get(pasteColIndex);

        // Update the row id for the cell if is the new row
        cell = isNewRow ? cell.set("rowId", rowId) : cell;

        if (column.cellType === TableCellTypes.label) {
          // label cells cannot be edited...
          return;
        }

        // If pasting ongoing is true, should not paste end date or duration
        if (pastedOngoing && (cell.columnId === outlineColumnIds.end || cell.columnId === outlineColumnIds.duration)) {
          return;
        }

        // If this is the end date and we have modified the start or duration already
        // we need to use those new values.
        // Note, we always sort the indexes above so that end comes last, so we can rely on
        // any changes to start/duration having already occured.
        if (cell.columnId === outlineColumnIds.end) {
          cell = pastedStart ? cell.set("startDate", pastedStart.value) : cell;
          cell = pastedDuration ? cell.set("duration", pastedDuration.value) : cell;
        }

        // Convert the text value into a value suitable for the cellType
        const value = pasteValueForCell(cell, column, calendar, rawValue, pasteDataType, getCellOptions);

        // If we can't get a valid value, and we are not trying to paste an empty cell
        // then we want to show the user an error and not perform the paste.
        if (isNil(value) && !isNilOrEmpty(rawValue)) {
          pasteErrorColumn = column.label;
          return;
        }

        // We use newState to determine whether it is a property or a reference...
        // Not ideal, but it will work for now.
        const newState =
          column.cellType === TableCellTypes.singleReference || column.cellType === TableCellTypes.multipleReference
            ? true
            : null;

        const pastedData = immutableRecords.TablePastedDataRecord({
          rowId: rowId,
          columnId: cell.columnId,
          value,
          newState
        });

        // We need to keep track of pasted date values so that we can apply them all at the end
        if (cell.columnId === outlineColumnIds.ongoing && value) {
          pastedOngoing = pastedData;
          bulkChanges.push(pastedData);
        } else if (cell.columnId === outlineColumnIds.start && value) {
          pastedStart = pastedData;
        } else if (cell.columnId === outlineColumnIds.duration && value) {
          pastedDuration = pastedData;
        } else if (cell.columnId === outlineColumnIds.end && value) {
          pastedEnd = pastedData;
        } else {
          // Send the changes to the Component using the table so it can figure out what to do with them
          bulkChanges.push(pastedData);
        }
      });

      // Now apply the dates altogether
      if (pastedStart) {
        // If they pasted a start date, apply that, and then add one of duration or end if required
        bulkChanges.push(pastedStart);
        // And then apply one of duration or end to go with it (favouring duration)
        if (pastedDuration) {
          bulkChanges.push(pastedDuration);
        } else if (pastedEnd) {
          bulkChanges.push(pastedEnd);
        }
      } else {
        // If they did not paste a start date, but they did paste both a duration and end
        // we need to calculate a new start date to take effect
        if (pastedDuration && pastedEnd) {
          const value = dateByAddingSpan(calendar, pastedEnd.value, getReverseTimespan(pastedDuration.value));
          pastedStart = immutableRecords.TablePastedDataRecord({
            rowId: rowId,
            columnId: outlineColumnIds.start,
            value
          });
          bulkChanges.push(pastedStart);
          bulkChanges.push(pastedDuration);
        } else if (pastedDuration) {
          bulkChanges.push(pastedDuration);
        } else if (pastedEnd) {
          bulkChanges.push(pastedEnd);
        }
      }
    });
  });

  // If any of the pasted cells would go beyond the right edge, then block it
  if (pasteDimensionsMismatch) {
    return immutableRecords.TablePasteResultRecord({
      success: false,
      errorMessage: interfaceMessages.TABLE_VIEW_PASTE_ERROR_WRONG_SIZE
    });
  }

  // If any of the pasted cells did not match the data type expected, then show the user an error
  // rather than allow a partial paste to go through.
  if (pasteErrorColumn) {
    return immutableRecords.TablePasteResultRecord({
      success: false,
      errorMessage: interfaceMessages.TABLE_VIEW_PASTE_ERROR_INVALID_DATA_TYPE(pasteErrorColumn)
    });
  }

  // Filter out the disabled changes if there are multiple cells changes
  if (bulkChanges.length > 1) {
    bulkChanges = bulkChanges.filter(c => c.columnId !== outlineColumnIds.identifier);
  }

  // If any of the pasted cells are disabled, then show the user an error
  // rather than allow a partial paste to go through.
  const changeOnDisabledCell = bulkChanges.find(change => {
    const changeRow = rows.find(r => r.id === change.rowId);
    // The change row might be the new row
    return changeRow ? changeRow.cells.find(c => c.columnId === change.columnId).disabled : false;
  });
  if (changeOnDisabledCell) {
    return immutableRecords.TablePasteResultRecord({
      success: false,
      errorMessage: interfaceMessages.TABLE_VIEW_PASTE_ERROR_DISABLED_COLUMN(
        columns.find(c => c.id === changeOnDisabledCell.columnId).label
      )
    });
  }

  return immutableRecords.TablePasteResultRecord({
    success: true,
    newRowIds: List(newRowIds),
    bulkChanges: List(bulkChanges)
  });
};

// ---------------------------------------------------------------------------------------------
// Deleting Data
// ---------------------------------------------------------------------------------------------

/**
 *
 * @param {List} columns
 * @param {List} rows
 * @param {TableCellSelectionRecord} cellSelection
 */
const bulkChangesForDeletion = (columns, rows, cellSelection) => {
  // List of changes we will apply.
  let bulkChanges = [];
  let deleteErrorColumn = null;

  cellSelection.groups.forEach(group => {
    // Turn them into ranges so we can iterate using functions
    const rowIndexes = range(group.topLeft.row, group.bottomRight.row + 1);
    const colIndexes = range(group.topLeft.column, group.bottomRight.column + 1);

    // Iterate over all rows
    rowIndexes.forEach(rowIndex => {
      // Cannot delete anything from the "create new" row...
      if (rowIndex >= rows.size || rows.get(rowIndex).id === tableRowTypes.EMPTY) {
        return;
      }

      const row = rows.get(rowIndex);

      // Iterate over all columns
      colIndexes.forEach(colIndex => {
        // Access the data associated with the cell so we can get the right type.
        const column = columns.get(colIndex);
        let cell = row.cells.get(colIndex);

        if (column.cellType === TableCellTypes.label) {
          // label cells cannot be edited...
          return;
        }

        if (!column.canDelete) {
          deleteErrorColumn = column.label;
          return;
        }

        // We use newState to determine whether it is a property or a reference...
        // Not ideal, but it will work for now.
        const newState =
          column.cellType === TableCellTypes.singleReference || column.cellType === TableCellTypes.multipleReference
            ? true
            : null;

        // We will just treat delete as the same as pasting empty data
        const changedData = immutableRecords.TablePastedDataRecord({
          rowId: row.id,
          columnId: cell.columnId,
          value: null,
          newState
        });
        // Send the changes to the Component using the table so it can figure out what to do with them
        bulkChanges.push(changedData);
      });
    });
  });

  // If any of the pasted cells did not match the data type expected, then show the user an error
  // rather than allow a partial paste to go through.
  if (deleteErrorColumn) {
    return immutableRecords.TablePasteResultRecord({
      success: false,
      errorMessage: interfaceMessages.TABLE_VIEW_DELETE_ERROR_INVALID_COLUMN(deleteErrorColumn)
    });
  }
  return immutableRecords.TablePasteResultRecord({
    success: true,
    newRowIds: List(),
    bulkChanges: List(bulkChanges)
  });
};

const isZeroCell = cell => cell.row === 0 && cell.column === 0;

const areCellsSame = (cellA, cellB, dataA, dataB) => {
  if (cellA) {
    if (cellB) {
      const { row: rowAIndex, column: colAIndex } = cellA;
      const { row: rowBIndex, column: colBIndex } = cellB;
      const rowAId = dataA.rows.get(rowAIndex).id;
      const rowBId = dataB.rows.get(rowBIndex).id;
      const colAId = dataA.columns.get(colAIndex).id;
      const colBId = dataB.columns.get(colBIndex).id;
      return rowAId === rowBId && colAId === colBId;
    } else {
      return false;
    }
  } else if (cellB) {
    return false;
  } else {
    return true;
  }
};

const getStickyColumnsCount = (columns, maxWidth, fontSize = Dimensions.BASE_FONT_SIZE) => {
  let resultCount = 0;
  if (!preprocessorQuery.isIos()) {
    // In desktop mode, find the sticky columns.
    // Count the number and total width
    const { width, count } = columns.reduce(
      (sum, c) => {
        if (stickyColumnIds.includes(c.id)) {
          sum.count += 1;
          sum.width += c.width;
        }
        return sum;
      },
      { width: 0, count: 0 }
    );
    const scaledMinWidth = scalePixelsToFontSize(OUTLINE_MIN_SCROLLABLE_COLUMNS_WIDTH, fontSize);
    resultCount = width > maxWidth - scaledMinWidth ? 0 : count;
  }
  return resultCount;
};

const tableViewUtils = {
  isCellSelected,
  isRowPartOfSelection,
  isCellInGroup,
  isRowInGroup,
  rowsInSelection,
  isCellSingleSelection,
  mergeSelectionIfRect,
  selectionWithoutCell,

  isZeroCell,
  areCellsSame,

  getStickyColumnsCount,

  // Clipboard Events
  copyTableDataToClipboard,
  pasteDataFromClipboard,

  // Delete Events
  bulkChangesForDeletion
};

export default tableViewUtils;
